import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { DonutProps } from './types';
import ChartWrapper from '../../ChartWrapper';

function Donut({
  data,
  chartName,
  showLabel,
  showLabelLine,
  showLegend,
  height,
  width,
  radius,
  center,
  grid,
  showZeroSum = true,
  onLegendSelect = () => {},
}: DonutProps) {
  const DEFAULT_STYLE = {
    height: height ?? 180,
    width: width ?? '100%',
  };

  const option: EChartsOption = useMemo(
    () => ({
      color: data.map(item => item.color),
      grid: grid,
      tooltip: {
        trigger: 'item',
        formatter: `${chartName} <br/>{b} : {c} ({d}%)`,
      },
      legend: {
        show: showLegend ?? true,
        top: '0%',
        orient: 'vertical',
        right: 'right',
        left: '50%',
        data: data.map(item => item.name),
        itemWidth: 12,
        itemHeight: 12,
        formatter: (name: string) => {
          const item = data.find(item => item.name === name);
          // pad the name with empty space to make it align
          const value = item?.value;
          // format should be 1M 1K 100
          const formattedValue = value
            ? new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value)
            : '0';
          return `${item?.name} {a|${formattedValue}}`;
        },
        textStyle: {
          fontSize: 13,
          fontWeight: 'lighter',
          fontFamily: 'Mulish',
          lineHeight: 18,
          width: 200,
          align: 'left',
          rich: {
            a: {
              align: 'right',
              fontSize: 13,
              fontWeight: 'lighter',
              fontFamily: 'Mulish',
              lineHeight: 18,
            },
          },
        },
        itemGap: 12,
      },
      series: [
        {
          name: 'Incident Count by Status',
          type: 'pie',
          center: center ?? [90, '50%'],
          radius: radius ?? ['60%', '90%'],
          stillShowZeroSum: showZeroSum,
          label: {
            show: showLabel ?? false,
            formatter: ({ data }) => {
              if (data && typeof data === 'object' && 'name' in data && 'value' in data) {
                return `${data?.name} \n  {a|(${data?.value})}`;
              }
              return '';
            },
            lineHeight: 18,
            rich: {
              a: {
                align: 'right',
                fontSize: 13,
                fontWeight: 'lighter',
                fontFamily: 'Mulish',
                lineHeight: 18,
              },
            },
          },
          // padAngle: 5,
          data: data.map(item => ({
            value: item.value,
            name: item.name,
          })),
          labelLine: {
            show: showLabelLine ?? false,
          },
        },
      ],
    }),
    [data],
  );

  const events = useMemo(() => {
    return {
      legendselectchanged: (param: any, echarts: any) => {
        onLegendSelect(param.name);
      },
    };
  }, []);

  return (
    <ChartWrapper>
      <ReactECharts option={option} style={DEFAULT_STYLE} onEvents={events} />
    </ChartWrapper>
  );
}

export default React.memo(Donut);
